import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    createNewArticleGroupCountryTax,
    deleteArticleGroupCountryTax,
    getArticleGroupCountryTax,
    updateArticleGroupCountryTax,
} from 'src/api/article-group-country-tax';
import { getArticleGroup } from 'src/api/article-management';
import {
    createNewArticleGroupGroupRebate,
    deleteArticleGroupGroupRebate,
    getGroupRebateByArticleTypeIdArticleGroupId,
    updateArticleGroupGroupRebate,
} from 'src/api/group-rebate';
import {
    CREATE_NEW_ARTICLE_COUNTRY_TAX,
    CREATE_NEW_GROUP_REBATE,
    DELETE_ARTICLE_COUNTRY_TAX,
    DELETE_GROUP_REBATE,
    fetchGroupRebates,
    FETCH_ARTICLE_COUNTRY_TAXES,
    FETCH_ARTICLE_GROUP,
    FETCH_GROUP_REBATES,
    requestArticleCountryTaxes,
    requestArticleCountryTaxesFailure,
    requestArticleCountryTaxesSuccess,
    requestArticleGroup,
    requestArticleGroupFailure,
    requestArticleGroupSuccess,
    requestCreateNewArticleCountryTax,
    requestCreateNewArticleCountryTaxFailure,
    requestCreateNewArticleCountryTaxSuccess,
    requestCreateNewGroupRebate,
    requestCreateNewGroupRebateFailure,
    requestCreateNewGroupRebateSuccess,
    requestDeleteArticleCountryTax,
    requestDeleteArticleCountryTaxFailure,
    requestDeleteArticleCountryTaxSuccess,
    requestDeleteGroupRebate,
    requestDeleteGroupRebateFailure,
    requestDeleteGroupRebateSuccess,
    requestGroupRebates,
    requestGroupRebatesFailure,
    requestGroupRebatesSuccess,
    requestUpdateArticleCountryTax,
    requestUpdateArticleCountryTaxFailure,
    requestUpdateArticleCountryTaxSuccess,
    requestUpdateGroupRebate,
    requestUpdateGroupRebateFailure,
    requestUpdateGroupRebateSuccess,
    UPDATE_ARTICLE_COUNTRY_TAX,
    UPDATE_GROUP_REBATE,
} from './actions';

function* fetchArticleGroupSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleGroup());

    const { error, response } = yield call(getArticleGroup, action.pageable, action.articleTypeId);

    if (error) {
        yield put(requestArticleGroupFailure(error));
    } else {
        yield put(requestArticleGroupSuccess(response));
    }
}

export function* generateFetchArticleGroupWatcher() {
    yield takeLatest(FETCH_ARTICLE_GROUP, fetchArticleGroupSaga);
}

/* Group Rebate */
function* fetchGroupRebatesSaga(action: AnyAction): SagaIterator {
    yield put(requestGroupRebates());

    const { error, response } = yield call(
        getGroupRebateByArticleTypeIdArticleGroupId,
        action.articleTypeId,
        action.articleGroupId
    );

    if (error) {
        yield put(requestGroupRebatesFailure(error));
    } else {
        yield put(requestGroupRebatesSuccess(response));
    }
}

function* createNewGroupRebateSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewGroupRebate());

    const { error, response } = yield call(createNewArticleGroupGroupRebate, action.groupRebateRequestDto);

    if (error) {
        yield put(requestCreateNewGroupRebateFailure(error));
    } else {
        yield put(requestCreateNewGroupRebateSuccess(response));
        yield put(
            fetchGroupRebates(action.groupRebateRequestDto.articleTypeId, action.groupRebateRequestDto.articleGroupId)
        );
    }
}

function* updateGroupRebateSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateGroupRebate());

    const { error, response } = yield call(updateArticleGroupGroupRebate, action.groupRebateRequestDto);

    if (error) {
        yield put(requestUpdateGroupRebateFailure(error));
    } else {
        yield put(requestUpdateGroupRebateSuccess(response));
        yield put(
            fetchGroupRebates(action.groupRebateRequestDto.articleTypeId, action.groupRebateRequestDto.articleGroupId)
        );
    }
}

function* deleteGroupRebateSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteGroupRebate());

    const { error, response } = yield call(
        deleteArticleGroupGroupRebate,
        action.articleTypeId,
        action.articleGroupId,
        action.minQuantity,
        action.premiumLevel
    );

    if (error) {
        yield put(requestDeleteGroupRebateFailure(error));
    } else {
        yield put(requestDeleteGroupRebateSuccess(response));
        yield put(fetchGroupRebates(action.articleTypeId, action.articleGroupId));
    }
}

export function* generateFetchGroupRebatesWatcher() {
    yield takeLatest(FETCH_GROUP_REBATES, fetchGroupRebatesSaga);
}

export function* generateCreateNewGroupRebateWatcher() {
    yield takeLatest(CREATE_NEW_GROUP_REBATE, createNewGroupRebateSaga);
}

export function* generateUpdateGroupRebateWatcher() {
    yield takeLatest(UPDATE_GROUP_REBATE, updateGroupRebateSaga);
}

export function* generateDeleteGroupRebateWatcher() {
    yield takeLatest(DELETE_GROUP_REBATE, deleteGroupRebateSaga);
}

/* Article Country Tax */
function* fetchArticleCountryTaxesSaga(action: AnyAction): SagaIterator {
    yield put(requestArticleCountryTaxes());

    const { error, response } = yield call(getArticleGroupCountryTax, action.articleTypeId, action.articleGroupId);

    if (error) {
        yield put(requestArticleCountryTaxesFailure(error));
    } else {
        yield put(requestArticleCountryTaxesSuccess(response));
    }
}

function* createNewArticleCountryTaxSaga(action: AnyAction): SagaIterator {
    yield put(requestCreateNewArticleCountryTax());

    const { error, response } = yield call(createNewArticleGroupCountryTax, action.countryTaxRequest);

    if (error) {
        yield put(requestCreateNewArticleCountryTaxFailure(error));
    } else {
        yield put(requestCreateNewArticleCountryTaxSuccess(response));
    }
}

function* updateArticleCountryTaxSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateArticleCountryTax());

    const { error, response } = yield call(updateArticleGroupCountryTax, action.countryTaxRequest);

    if (error) {
        yield put(requestUpdateArticleCountryTaxFailure(error));
    } else {
        yield put(requestUpdateArticleCountryTaxSuccess(response));
    }
}

function* deleteArticleCountryTaxSaga(action: AnyAction): SagaIterator {
    yield put(requestDeleteArticleCountryTax());

    const { error, response } = yield call(deleteArticleGroupCountryTax, action.articleGroupCountryTaxId);

    if (error) {
        yield put(requestDeleteArticleCountryTaxFailure(error));
    } else {
        yield put(requestDeleteArticleCountryTaxSuccess(response));
    }
}

export function* generateFetchArticleCountryTaxesWatcher() {
    yield takeLatest(FETCH_ARTICLE_COUNTRY_TAXES, fetchArticleCountryTaxesSaga);
}

export function* generateCreateNewArticleCountryTaxWatcher() {
    yield takeLatest(CREATE_NEW_ARTICLE_COUNTRY_TAX, createNewArticleCountryTaxSaga);
}

export function* generateUpdateArticleCountryTaxWatcher() {
    yield takeLatest(UPDATE_ARTICLE_COUNTRY_TAX, updateArticleCountryTaxSaga);
}

export function* generateDeleteArticleCountryTaxWatcher() {
    yield takeLatest(DELETE_ARTICLE_COUNTRY_TAX, deleteArticleCountryTaxSaga);
}
